import React from "react";
import { Check, Layers, ShieldCheck, Smartphone } from "lucide-react";
import {
  CREDENTIAL_FORMAT,
  CREDENTIAL_FORMAT_OPTIONS,
} from "../utils/issuerProject";

const FORMAT_CONFIG = {
  [CREDENTIAL_FORMAT.W3C_VC]: {
    id: "cert-onboarding-format-w3c-vc",
    Icon: ShieldCheck,
    titleKey: "certificateOnboarding:credential_format_w3c_vc_title",
    subtitleKey: "certificateOnboarding:credential_format_w3c_vc_subtitle",
    bulletKeys: [
      "certificateOnboarding:credential_format_w3c_vc_bullet_1",
      "certificateOnboarding:credential_format_w3c_vc_bullet_2",
      "certificateOnboarding:credential_format_w3c_vc_bullet_3",
    ],
  },
  [CREDENTIAL_FORMAT.MSO_MDOC]: {
    id: "cert-onboarding-format-mso-mdoc",
    Icon: Smartphone,
    titleKey: "certificateOnboarding:credential_format_mso_mdoc_title",
    subtitleKey: "certificateOnboarding:credential_format_mso_mdoc_subtitle",
    bulletKeys: [
      "certificateOnboarding:credential_format_mso_mdoc_bullet_1",
      "certificateOnboarding:credential_format_mso_mdoc_bullet_2",
      "certificateOnboarding:credential_format_mso_mdoc_bullet_3",
    ],
  },
};

function FormatOptionCard({
  id,
  format,
  selected,
  onSelect,
  icon: Icon,
  title,
  subtitle,
  bullets,
  disabled = false,
}) {
  return (
    <button
      type="button"
      id={id}
      onClick={() => onSelect(format)}
      disabled={disabled}
      aria-pressed={selected}
      className={`w-full text-left p-4 sm:p-5 rounded-xl border-2 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary disabled:cursor-not-allowed disabled:opacity-60 ${
        selected
          ? "border-cfPrimary bg-cfPrimaryMuted shadow-sm"
          : "border-cfCardBorder bg-cfSecondary hover:border-cfPrimary/40 hover:bg-cfCardHover"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            selected
              ? "bg-cfPrimaryMuted"
              : "bg-cfSecondary border border-cfCardBorder"
          }`}
        >
          <Icon
            className={`w-5 h-5 ${selected ? "text-cfPrimary" : "text-cfGlobalText"}`}
            aria-hidden
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-cfHeadingText">{title}</p>
            {selected && (
              <span
                className="shrink-0 w-5 h-5 rounded-full bg-cfPrimary flex items-center justify-center"
                aria-hidden
              >
                <Check className="w-3 h-3 text-cfSecondary" strokeWidth={3} />
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-xs text-cfGlobalText mt-1 leading-relaxed">
              {subtitle}
            </p>
          )}
          {bullets.length > 0 && (
            <ul className="mt-2.5 space-y-1">
              {bullets.map((bullet) => (
                <li
                  key={bullet}
                  className="text-xs text-cfGlobalText leading-relaxed flex items-start gap-1.5"
                >
                  <span
                    className={`shrink-0 mt-1.5 w-1 h-1 rounded-full ${
                      selected ? "bg-cfPrimary" : "bg-cfGlobalText/50"
                    }`}
                    aria-hidden
                  />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </button>
  );
}

function CredentialFormatPicker({ value, onChange, error, disabled = false, t }) {
  return (
    <section
      id="cert-onboarding-credential-format"
      className="rounded-xl border border-cfCardBorder bg-cfSecondary shadow-sm p-4 sm:p-6 mb-4"
      aria-labelledby="cert-onboarding-credential-format-heading"
    >
      <div className="mb-4 pb-4 border-b border-cfCardBorder flex items-center gap-2.5">
        <div className="shrink-0 w-8 h-8 rounded-lg bg-cfPrimary flex items-center justify-center">
          <Layers className="w-4 h-4 text-cfSecondary" strokeWidth={2} aria-hidden />
        </div>
        <div>
          <h2
            id="cert-onboarding-credential-format-heading"
            className="text-base font-semibold text-cfHeadingText leading-tight"
          >
            {t("certificateOnboarding:credential_format_section_title")}
            <span className="text-cfStatusBlockedText ml-1" aria-hidden="true">
              *
            </span>
          </h2>
          <p className="text-xs text-cfGlobalText mt-0.5">
            {t("certificateOnboarding:credential_format_section_subtitle")}
          </p>
        </div>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
        role="group"
        aria-labelledby="cert-onboarding-credential-format-heading"
        aria-describedby={error ? "cert-onboarding-credential-format-error" : undefined}
      >
        {CREDENTIAL_FORMAT_OPTIONS.map((format) => {
          const config = FORMAT_CONFIG[format];
          return (
            <FormatOptionCard
              key={format}
              id={config.id}
              format={format}
              selected={value === format}
              onSelect={onChange}
              icon={config.Icon}
              title={t(config.titleKey)}
              subtitle={t(config.subtitleKey)}
              bullets={config.bulletKeys.map((key) => t(key))}
              disabled={disabled}
            />
          );
        })}
      </div>

      {error && (
        <p
          id="cert-onboarding-credential-format-error"
          className="text-xs text-cfStatusBlockedText mt-3"
          role="alert"
        >
          {error}
        </p>
      )}
    </section>
  );
}

export default CredentialFormatPicker;
